import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import CalendarService from '../services/calendarService';
import toast from 'react-hot-toast';

const GoogleOAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('Processing your Google Calendar connection...');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          console.error('❌ Google OAuth error:', error, errorDescription);
          setStatus('error');
          setMessage(`Authentication failed: ${errorDescription || error}`);
          toast.error('Failed to connect Google Calendar');

          setTimeout(() => {
            navigate('/dashboard?section=API Keys & Integrations');
          }, 3000);
          return;
        }

        if (!code) {
          throw new Error('No authorization code received from Google');
        }

        console.log('✅ Received authorization code from Google');
        setMessage('Exchanging authorization code for access token...');

        const result = await CalendarService.handleGoogleCallback(code);

        if (result.success) {
          console.log('✅ Successfully obtained Google access token');
          setStatus('success');
          setMessage('🎉 Successfully connected to Google Calendar!');
          localStorage.setItem('google_calendar_connected', 'true');
          localStorage.setItem('google_calendar_connection_time', new Date().toISOString());

          toast.success('Google Calendar connected successfully!');

          if (window.opener) {
            window.opener.postMessage({ type: 'GOOGLE_OAUTH_SUCCESS', tokens: result }, '*');
            setTimeout(() => {
              window.close();
            }, 2000);
            return;
          }

          setTimeout(() => {
            navigate('/dashboard?section=API Keys & Integrations');
          }, 2000);
        } else {
          throw new Error(result.error || 'Failed to exchange code for token');
        }
      } catch (err) {
        console.error('❌ OAuth callback error:', err);
        setStatus('error');
        setMessage(`Connection failed: ${err.message}`);
        toast.error('Failed to connect Google Calendar');

        setTimeout(() => {
          navigate('/dashboard?section=API Keys & Integrations');
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="mb-6">
          {status === 'processing' && (
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          )}
          {status === 'success' && (
            <div className="text-green-500 text-6xl mb-4">✅</div>
          )}
          {status === 'error' && (
            <div className="text-red-500 text-6xl mb-4">❌</div>
          )}
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {status === 'processing' && 'Connecting to Google Calendar...'}
          {status === 'success' && 'Connection Successful!'}
          {status === 'error' && 'Connection Failed'}
        </h2>

        <p className="text-gray-600 mb-6">{message}</p>

        {(status === 'success' || status === 'error') && (
          <button
            onClick={() => navigate('/dashboard?section=API Keys & Integrations')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Return to Integrations
          </button>
        )}
      </div>
    </div>
  );
};

export default GoogleOAuthCallback;
